import * as Vue from 'vue';
import {
    createRouter,
    createWebHistory,
    type NavigationGuard,
    type NavigationGuardNext,
    type RouteLocationNormalized,
    type RouteLocationRaw
} from 'vue-router'
import ContainerView from '@/views/ContainerView.vue'
import DbgBenefitsView from '@/views/DbgBenefitsView.vue'
import ArrotexValueView from '@/views/ArrotexValueView.vue'
import VidacorpValueView from '@/views/VidacorpValueView.vue'
import AxeValueView from '@/views/AxeValueView.vue'
import ArrotexOffersView from '@/views/ArrotexOffersView.vue'
import VidacorpOffersView from '@/views/VidacorpOffersView.vue'
import AxeOffersView from '@/views/AxeOffersView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import LandingView from '@/views/LandingView.vue'
import DbgAboutView from '../views/DbgAboutView.vue'
import AuthCallback from '../views/AuthCallback.vue'

import {useAuthStore, useValueDataStore} from '@/stores'
import ExportToPdf from '@/components/export/ExportToPdf.vue'
import {storeToRefs} from "pinia"
import AuthErrorView from "@/views/AuthErrorView.vue"
import FaqsView from '@/views/FaqsView.vue'
import StoresListSwitch from '@/views/StoresListSwitch.vue'
import GroupEditView from "@/views/GroupEditView.vue";
import PermissionsView from "@/views/PermissionsView.vue";
import {RoleService} from "@/services";
import AssignmentsUploadForm from "@/views/AssignmentsUploadForm.vue";
import DatasetsListView from "@/views/DatasetsListView.vue";
import DatasetCreateView from "@/views/DatasetCreateView.vue";
import OrganisationView from "@/views/OrganisationView.vue";
import BusinessDevelopmentView from "@/views/BusinessDevelopmentView.vue";
import NotesActivitiesView from "@/views/NotesActivitiesView.vue";
import SalesOrdersView from "@/views/SalesOrdersView.vue";

const guardSecure: NavigationGuard = async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: (to?: RouteLocationRaw | false | ((vm: typeof Vue) => any) | void) => void
) => {
    const authStore = useAuthStore();
    const { isLoggedIn,  } = storeToRefs(authStore);

    const valueDataStore  = useValueDataStore();
    const { handleLogin } = valueDataStore;
    const { isValueDataLoaded } = storeToRefs(valueDataStore);

    if (!isLoggedIn.value) {
        await authStore.silentLogin();
    }

    if (isLoggedIn.value) {
        if (!isValueDataLoaded.value) {
            // not awaiting, just calling
            handleLogin().then(() => {});
        }
        return next();
    } else {
        await authStore.login({targetUrl: to.fullPath});
        return; // stop navigation
    }
}

const guardLogin: NavigationGuard = async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
) => {

    const authStore = useAuthStore();
    const { isLoggedIn } = storeToRefs(authStore);

    const valueDataStore  = useValueDataStore();
    const { handleLogin } = valueDataStore;
    const { isValueDataLoaded } = storeToRefs(valueDataStore);

    if (isLoggedIn.value) {
        next({ path: "/benefits"});
    }

    if (!isLoggedIn.value) {
        await authStore.silentLogin();
    }

    if (isLoggedIn.value) {
        if (!isValueDataLoaded.value) {
            // not awaiting, just calling
            handleLogin().then(() => {});
        }
        next({ path: "/benefits"});
    } else {
        return next();
    }
}

 const isAdminGuard = async (
     to: RouteLocationNormalized,
     from: RouteLocationNormalized,
     next: NavigationGuardNext
 ) => {
    const authStore = useAuthStore();
    const { isAuthLoading } = storeToRefs(authStore);
    const { uiRoles } = storeToRefs(useValueDataStore());

    // Wait for auth loading to complete
    while (isAuthLoading.value) {
        console.log("Waiting for auth to load...");
        await new Promise(resolve => setTimeout(resolve, 50));
    }

     // Wait for roles loading to complete
    while (!uiRoles?.value.length) {
        console.log("Waiting for roles ...");
        await new Promise(resolve => setTimeout(resolve, 50));
    }

    const roleService = new RoleService();
    const hasRole = roleService.hasRole("administrator");

    if (!hasRole) {
        console.warn("Access denied - Admins only");
        next({name: "404"});
    } else {
        next();
    }
 }

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/index.html',
            redirect: '/'
        },
        {
            path: '/',
            name: 'root',
            beforeEnter: guardSecure,
            component: ContainerView,
            children: [
                {
                    path: '',
                    name: 'dashboard home (root)',
                    redirect: '/about'
                },
                {
                    path: 'about',
                    name: 'about',
                    beforeEnter: guardSecure,
                    component: DbgAboutView
                },
                {
                    path: 'benefits',
                    name: 'benefits',
                    beforeEnter: guardSecure,
                    component: DbgBenefitsView
                },
                {
                    path: 'benefits/arrotex',
                    name: 'arrotex value',
                    beforeEnter: guardSecure,
                    component: ArrotexValueView
                },
                {
                    path: 'benefits/vidacorp',
                    name: 'vidacorp value',
                    beforeEnter: guardSecure,
                    component: VidacorpValueView
                },
                {
                    path: 'benefits/axe',
                    name: 'axe value',
                    beforeEnter: guardSecure,
                    component: AxeValueView
                },
                {
                    path: 'offers/arrotex',
                    name: 'arrotex offers',
                    beforeEnter: guardSecure,
                    component: ArrotexOffersView
                },
                {
                    path: 'offers/vidacorp',
                    name: 'vidacorp offers',
                    beforeEnter: guardSecure,
                    component: VidacorpOffersView
                },
                {
                    path: 'offers/axe',
                    name: 'axe offers',
                    beforeEnter: guardSecure,
                    component: AxeOffersView
                },
                {
                    path: 'organisation',
                    name: 'organisation',
                    beforeEnter: guardSecure,
                    component: OrganisationView
                },
                {
                    path: 'organisation/business-development',
                    name: 'organisation-bd',
                    beforeEnter: guardSecure,
                    component: BusinessDevelopmentView
                },
                {
                    path: 'organisation/notes-activities',
                    name: 'organisation-notes',
                    beforeEnter: guardSecure,
                    component: NotesActivitiesView
                },
                {
                    path: 'organisation/sales-orders',
                    name: 'organisation-sales-orders',
                    beforeEnter: guardSecure,
                    component: SalesOrdersView
                },
                {
                    path: 'faqs',
                    name: 'faqs',
                    beforeEnter: guardSecure,
                    component: FaqsView
                },
                {
                    path: 'stores-list',
                    name: 'storesList',
                    beforeEnter: guardSecure,
                    component: StoresListSwitch
                },
                {
                    path: 'export',
                    name: 'export',
                    beforeEnter: guardSecure,
                    component: ExportToPdf,
                    props: {
                          dbgNetValue: true,
                          arrotexValue: true,
                          vidacorpValue: true,
                          axeValue: true,
                    }
                },
                {
                    path: 'admin',
                    name: 'admin',
                    beforeEnter: [guardSecure, isAdminGuard],
                    children: [
                        {
                            path: 'groups/:groupId/edit',
                            name: 'group-edit',
                            component: GroupEditView,
                            children: []
                        },
                        {
                            path: 'permissions',
                            name: 'permissions',
                            component: PermissionsView,
                            children: []
                        },
                        {
                            path: 'assignments',
                            name: 'Assignments',
                            component: AssignmentsUploadForm,
                            children: []
                        },
                        {
                            path: 'datasets',
                            name: 'datasets-list',
                            component: DatasetsListView,
                            children: []
                        },
                        {
                            path: 'datasets/new',
                            name: 'dataset-create',
                            component: DatasetCreateView,
                            children: []
                        }
                    ]
                },
                {
                    path: '/404',
                    name: '404',
                    component: NotFoundView
                },
                {
                    path: '/:catchAll(.*)',
                    name: '404Name',
                    beforeEnter: guardSecure,
                    component: NotFoundView
                }
            ]
        },
        {
            path: '/auth/callback',
            component: AuthCallback
        },
        {
            path: '/autherror',
            component: AuthErrorView
        },
        {
            path: '/welcome',
            name: 'welcome',
            beforeEnter: guardLogin,
            component: LandingView
        }
    ]
});

export default router
